import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { MainHeading } from "@/components/common/MainHeading";
import { TableFilters } from "@/components/common/TableFilters";
import { Layout } from "@/components/Layout/Layout";
import { useFilter } from "@/hooks/useFilter";
import CommonTable, { TableColumn, Action } from "@/components/common/CommonTable";
import { Ipayout, IPayoutResponseList } from "@/interfaces/Payouts/payout.interface";
import { GetFilterUrl, formatRand, downloadExcel } from "@/utility/helper";
import { useEffect, useState } from "react";
import { StatusBadge } from "@/components/common/StatusBadge";
import Pagination from "@/components/common/Pagination";
import { PayoutDetailsModal } from "@/components/common/Model/PayoutDetailsModal";
import { Eye, Download } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";
import DateRangePicker from "@/components/common/DateRange";
import { Range } from "react-date-range";
import { format, startOfMonth, endOfMonth } from "date-fns";
import Button from "@/components/common/Button";
import { CommonSelect } from "@/components/common/CommonSelect";

// Default to current month
const now = new Date();
const DEFAULT_START = format(startOfMonth(now), "yyyy-MM-dd");
const DEFAULT_END = format(endOfMonth(now), "yyyy-MM-dd");

export const Payouts = () => {
    const [payouts, setPayouts] = useState<Ipayout[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingExport, setLoadingExport] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPayout, setSelectedPayout] = useState<Ipayout | null>(null);
    const { canApprovedReject, canExport } = usePermissions();

    const { filters, setPageInfo, debouncedFilters, handlePageChange, pageInfo, updateFilter } = useFilter({
        page: 1,
        limit: 10,
        search: '',
        status: '',
        startDate: DEFAULT_START,
        endDate: DEFAULT_END,
    });

    // Initialise DateRangePicker with current-month selection
    const [dateRange, setDateRange] = useState<Range[]>([
        {
            startDate: startOfMonth(now),
            endDate: endOfMonth(now),
            key: 'selection',
        },
    ]);

    const handleDateChange = (newRange: any) => {
        const resolved = typeof newRange === 'function' ? newRange(dateRange) : newRange;
        setDateRange(resolved);

        const start = resolved[0]?.startDate;
        const end = resolved[0]?.endDate;
        const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
        const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

        if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
            updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
        }
    };

    useEffect(() => {
        getPayouts();
    }, [
        debouncedFilters.page,
        debouncedFilters.limit,
        debouncedFilters.search,
        debouncedFilters.status,
        debouncedFilters.startDate,
        debouncedFilters.endDate,
    ]);

    const getPayouts = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl({ ...debouncedFilters, status: debouncedFilters.status === 'all' ? '' : debouncedFilters.status });
            const response = await callApi<IPayoutResponseList>(
                `${API_URL}${API_ENDPOINT.payouts}/all-requests?${queryParams}`,
                { method: 'GET' }
            );
            setPayouts(response?.data?.payouts ?? []);
            setPageInfo(response?.data?.pageInfo ?? { limit: 10, page: 1, total: 0, totalPages: 0 });
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        const filterParams = {
            search: debouncedFilters.search,
            status: debouncedFilters.status === 'all' ? '' : debouncedFilters.status,
            startDate: debouncedFilters.startDate,
            endDate: debouncedFilters.endDate,
        };
        const queryParams = GetFilterUrl(filterParams);
        const url = `${API_URL}${API_ENDPOINT.payouts}/export/excel?${queryParams}`;
        setLoadingExport(true);
        try {
            await downloadExcel(url, `payouts_${filterParams.startDate}_${filterParams.endDate}.xlsx`);
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingExport(false);
        }
    };

    const columns: TableColumn<Ipayout>[] = [
        {
            key: "payoutRef",
            header: "Payout Reference",
            render: (item) => (
                <span className="font-medium text-primary font-mono">{item.payoutRef}</span>
            )
        },
        {
            key: "amount",
            header: "Amount",
            render: (item) => (
                <span className="font-bold text-foreground">{formatRand(item.amount)}</span>
            )
        },
        {
            key: "note",
            header: "Note",
            render: (item) => (
                <span className="text-sm text-muted-foreground max-w-xs truncate">{item.note || '---'}</span>
            )
        },
        {
            key: "driver",
            header: "Driver",
            render: (item) => {
                const customerName = item.driver?.fullName || '---';
                const phoneNumber = item.driver?.phone || '---';
                return (
                    <div className="flex flex-col">
                        <span className="font-medium text-foreground">{customerName}</span>
                        <span className="text-xs text-muted-foreground">{phoneNumber}</span>
                    </div>
                );
            }
        },
        {
            key: "createdAt",
            header: "Date",
            render: (item) => (
                <span className="text-sm text-muted-foreground">
                    {item.createdAt ? format(new Date(item.createdAt), 'dd MMM yyyy') : '---'}
                </span>
            )
        },
        {
            key: "status",
            header: "Status",
            render: (item) => (
                <StatusBadge status={item.status} />
            )
        }
    ];

    const actions: Action<Ipayout>[] = [
        {
            text: 'View Details',
            icon: <Eye size={16} strokeWidth={1.5} className="cursor-pointer" />,
            onClick: (record: Ipayout) => {
                setSelectedPayout(record);
                setIsModalOpen(true);
            },
        }
    ];

    return (
        <Layout>
            <MainHeading
                title="Driver Payouts"
                description="Manage your payouts and view transaction history."
            />

            <div className="mt-6 flex flex-col gap-4">
                {/* Filters row */}
                <TableFilters
                    placeholder="Search Payouts"
                    searchTerm={filters.search}
                    onSearchChange={(query) => updateFilter({ search: query })}
                >

                    <div className="flex items-center gap-3 flex-wrap">
                        <CommonSelect
                            value={filters.status || ''}
                            onValueChange={(val) => updateFilter({ status: val, page: 1 })}
                            placeholder="Select Statuses"
                            options={[
                                { value: 'all', label: 'All Statuses' },
                                { value: 'pending', label: 'Pending' },
                                { value: 'approved', label: 'Approved' },
                                { value: 'rejected', label: 'Rejected' },
                            ]}
                        />
                        <DateRangePicker
                            dateRange={dateRange}
                            setDateRange={handleDateChange as any}
                        />
                        {canExport(RESOURCES.PAYOUTS) && (
                            <Button
                                variant="outline"
                                onClick={handleExport}
                                className="h-10 px-4 flex items-center gap-2 rounded-xl"
                                leftIcon={<Download className="w-4 h-4" />}
                                isLoading={loadingExport}
                            >
                                Export
                            </Button>
                        )}
                    </div>
                </TableFilters>


                <CommonTable
                    columns={columns}
                    data={payouts}
                    loading={loading}
                    actions={actions}
                    emptyMessage="No payout records found for the selected date range."
                />

                <Pagination
                    currentPage={filters.page || 1}
                    totalPages={pageInfo.totalPages}
                    totalItems={pageInfo.total}
                    itemsPerPage={pageInfo.limit}
                    onPageChange={handlePageChange}
                />
            </div>

            <PayoutDetailsModal
                open={isModalOpen}
                onOpenChange={setIsModalOpen}
                payout={selectedPayout}
                onSuccess={getPayouts}
            />
        </Layout>
    );
};
